<template>
  <div>
    <card-empty-state v-if="isEmpty" icon="table" height="166px" class="mt-4" />
    <div v-else class="details-container">
      <span>Detalhamento do faturamento incremental - {{ formattedDateLabel }}</span>

      <div class="details-table">
        <div class="headers">
          <div></div>
          <div v-for="(header, index) in headers" :key="index" class="grid-item">
            <mf-tooltip :disabled="!headerTooltips[index].title" max-width="300" top variant="secondary">
              <template #activator="{ on }">
                <div class="header-text d-flex align-center">
                  {{ header }} <mf-icon v-if="headerTooltips[index].title" class="ml-1" dense color="marca-0-l10" icon="info" />
                </div>
              </template>
              <div class="header-tooltip">
                <h2 class="tooltip-title">{{ headerTooltips[index].title }}</h2>
                <p class="tooltip-text">{{ headerTooltips[index].description }}</p>
              </div>
            </mf-tooltip>
          </div>
        </div>
        <div class="clients-data">
          <div class="header-text">Núm. de clientes</div>
          <div v-for="(clientMetric, index) in clients_values" :key="`${clientMetric}-${index}-client`" class="grid-item">
            {{ localeString(clientMetric) }}
            <mf-quick-actions
              ref="quickAction"
              :key="`dre-incremental-revenue-${index}`"
              :quick-action-group-id="`dre-incremental-revenue-${index}`"
              :short-description="quickActionLabel(index)"
              :long-description="quickActionLabel(index)"
              :segmentation-group="criteriaGroup(dreIncrementalRevenueDetailsCriteria(analyticsConfiguration._id, drePeriodId, index))"
              tooltip="Algumas divergências podem ser encontradas nos valores das ações rápidas devido ao descadastramento de consumidores durante o período analisado do Faturamento incremental."
            />
          </div>
        </div>
        <div class="revenue-data">
          <div class="header-text">Fat. Produtos</div>
          <div v-for="(revenueMetric, index) in products_revenues_values" :key="`${revenueMetric}-${index}-product-revenue`" class="grid-item">
            {{ `R$${localeString(revenueMetric, 0)}` }}
          </div>
        </div>
        <div class="increment-data">
          <div class="header-text">Incremento</div>
          <div v-for="(incrementMetric, index) in increment_values" :key="`${incrementMetric}-${index}-increment`" class="grid-item">
            <mf-tooltip :disabled="!incrementTooltips[index].title" max-width="300" top variant="tertiary" color="verde-0">
              <template #activator="{ on }">
                <variation-number :value="incrementMetric" is-currency />
              </template>
              <div class="increment-tooltip">
                <h2 class="tooltip-title">Incremento</h2>
                <span>{{ incrementTooltips[index].title }}</span>
                <p class="tooltip-text">{{ incrementTooltips[index].description }}</p>
              </div>
            </mf-tooltip>
          </div>
        </div>
        <div class="discount-data">
          <div class="header-text">Descontos</div>
          <div v-for="(discountMetric, index) in discount_values" :key="`${discountMetric}-${index}-discount`" class="grid-item">
            <mf-tooltip :disabled="index === 4" max-width="300" top variant="tertiary" color="vermelho-d20">
              <template #activator="{ on }">
                <variation-number :value="discountMetric" variant="negative" is-currency />
              </template>
              <div class="discount-tooltip">
                <h2 class="tooltip-title">Desconto</h2>
                <p class="tooltip-text">O desconto é calculado com base nos produtos ofertados e comprados pelos clientes.</p>
              </div>
            </mf-tooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { abbreviation, localeString } from '@/config/helpers.js'
import { mapGetters } from 'vuex'
import { criteriaGroup, dreIncrementalRevenueDetailsCriteria } from '@/components/quick-actions/segmentationGroups.js'
import VariationNumber from '@/components/VariationNumber.vue'
import CardEmptyState from '@/components/CardEmptyState.vue'

export default {
  name: 'IncrementalRevenueDetails',
  components: { VariationNumber, CardEmptyState },
  props: {
    formattedDateLabel: {
      type: String,
      default: '',
    },
    drePeriodId: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters(['dreRevenueIncrementMetrics', 'analyticsConfiguration']),
    metrics() {
      return this.dreRevenueIncrementMetrics?.detailsMetrics?.clusterMetrics
    },
    totalMetrics() {
      return this.dreRevenueIncrementMetrics?.detailsMetrics?.total
    },
    actives() {
      return this.metrics?.actives
    },
    firstBuy() {
      return this.metrics?.firstBuy
    },
    inactives() {
      return this.metrics?.inactives
    },
    losses() {
      return this.metrics?.losses
    },
    clients_values() {
      if (this.totalMetrics) {
        const { totalClients } = this.totalMetrics

        return [this.actives?.totalClients, this.inactives?.totalClients, this.losses?.totalClients, this.firstBuy?.totalClients, totalClients]
      } else return []
    },
    products_revenues_values() {
      if (this.totalMetrics) {
        const { productsRevenue } = this.totalMetrics

        return [this.actives?.productsRevenue, this.inactives?.productsRevenue, this.losses?.productsRevenue, this.firstBuy?.productsRevenue, productsRevenue]
      } else return []
    },
    increment_values() {
      if (this.totalMetrics) {
        const { increment } = this.totalMetrics

        return [this.actives?.increment, this.inactives?.increment, this.losses?.increment, this.firstBuy?.increment, increment]
      } else return []
    },
    discount_values() {
      if (this.totalMetrics) {
        const { discount } = this.totalMetrics

        return [this.actives?.discount, this.inactives?.discount, this.losses?.discount, this.firstBuy?.discount, discount]
      } else return []
    },
    headers() {
      return ['Clientes recorrentes', 'Upsell', 'Clientes recuperados', 'Novos clientes', 'Total']
    },
    headerTooltips() {
      return [
        {
          title: 'Clientes recorrentes',
          description: 'Clientes que compraram algum dos produtos ofertados nos 90 dias anteriores a análise.',
        },
        {
          title: 'Upsell',
          description: 'Clientes que não compraram os produtos ofertados nos 90 dias anteriores a análise, mas compraram outros produtos.',
        },
        {
          title: 'Clientes recuperados',
          description: 'Clientes perdidos que não compraram nos 90 dias anteriores a análise.',
        },
        {
          title: 'Novos clientes',
          description: 'Clientes que realizaram a primeira compra no período da análise.',
        },
        {
          title: '',
          description: '',
        },
      ]
    },
    incrementTooltips() {
      return [
        {
          title: 'Clientes recorrentes',
          description:
            'É calculado com base em um aumento no consumo habitual desses clientes. Este cálculo é feito através da média diária de compras do produto específico ofertado por cada cliente nos últimos 90 dias no clube. Se um cliente excede essa média durante o período de uma oferta, interpretamos esse aumento como um incremento. Este incremento pode ser o resultado de uma variedade de fatores, incluindo a atratividade da oferta, as comunicações feitas ao cliente, entre outros impulsionadores de consumo.',
        },
        {
          title: 'Upsell',
          description:
            'São clientes que experimentaram produtos em oferta que não constavam em seu histórico de compras dos últimos 90 dias no clube. Essa mudança no comportamento de compra pode ser atribuída à atratividade da oferta, ao impacto das comunicações direcionadas ou a outros fatores que despertaram o interesse do cliente.',
        },
        {
          title: 'Clientes recuperados',
          description:
            'É calculado com base na recuperação de clientes que estavam perdidos, ou seja, que não fizeram compras nos últimos 90 dias no clube. Lembrando que estamos considerando todo o incremento da venda, pois esse cliente estava perdido, ou seja, poderia não realizar nenhuma compra se não fosse recuperado pela oferta. A recuperação desses clientes pode ser atribuída a vários fatores, como a atratividade da oferta e o impacto das comunicações personalizadas realizadas.',
        },
        {
          title: 'Novos clientes',
          description:
            'É calculado com base na conquista de novos clientes, ou seja, que não haviam realizado compras até então no clube. Lembrando que estamos considerando todo o incremento da venda, pois esse cliente foi convertido pela oferta. A conquista desses clientes pode ser atribuída a diversos fatores, como a atratividade da oferta.',
        },
        {
          title: '',
          description: '',
        },
      ]
    },
    isEmpty() {
      return this.metrics === undefined
    },
  },
  methods: {
    abbreviation,
    localeString,
    criteriaGroup,
    dreIncrementalRevenueDetailsCriteria,
    quickActionLabel(index) {
      const labels = {
        0: 'Faturamento incremental - Clientes recorrentes',
        1: 'Faturamento incremental - Upsell',
        2: 'Faturamento incremental - Clientes recuperados',
        3: 'Faturamento incremental - Novos clientes',
        4: 'Faturamento incremental - Total',
      }

      return labels[index]
    },
  },
}
</script>

<style lang="scss" scoped>
.details-container {
  display: flex;
  flex-direction: column;
  gap: 32px;
  span {
    color: $cinza-0;
    font-size: 16px;
    font-weight: 400;
    line-height: 120%;
    letter-spacing: 0.4px;
  }
  .details-table {
    width: 100%;
    display: grid;
    .headers,
    .clients-data,
    .revenue-data,
    .increment-data,
    .discount-data {
      height: 52px;
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 20px;
      align-items: center;
    }
    .headers {
      align-items: baseline;
      height: auto !important;
    }
    .revenue-data,
    .increment-data,
    .discount-data {
      border-top: 1px solid #d2d6d9;
    }
  }
  .header-text {
    color: $cinza-d20;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: 0.14px;
  }
}
::v-deep .v-data-table th {
  padding: 15px 8px;
  span {
    color: $cinza-d20;
    font-size: 14px;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: 0.14px;
  }
}

.header-tooltip {
  h2 {
    color: $cinza-d20;
    margin-bottom: 4px;
  }
}

.increment-tooltip,
.discount-tooltip {
  h2 {
    color: $verde-0;
    line-height: 1;
    margin-bottom: 0px;
  }

  span {
    color: $verde-0;
    display: inline-block;
    margin-bottom: 4px;
    font-size: 12px;
  }
}

.discount-tooltip {
  h2 {
    color: $vermelho-d20;
    margin-bottom: 8px;
  }
}
.tooltip-title {
  font-weight: 700;
  font-size: 14px;
  line-height: 16.8px;
}

.tooltip-text {
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: $cinza-0;
  margin-bottom: 0px;
}

.grid-item:last-child {
  height: 100%;
  display: flex;
  align-items: center;
  padding-left: 20px;
  border-left: 2px solid $marca-0;
}
</style>
